import { cn } from "@nexxt/ui";
import { type ReactNode, useRef } from "react";
import { m } from "../../paraglide/messages.js";
import { useInView } from "../motion/use-in-view";

interface ChapterProps {
  id: string;
  /** The app the chapter is about, or the world around them: the colour of its kicker and points. */
  app: string;
  number: number;
  label: string;
  audience: string;
  headline: string;
  body: string;
  points: readonly string[];
  flipped?: boolean;
  /** The scene beside the words: it plays only while it is on screen. */
  scene: (active: boolean) => ReactNode;
}

/** One ring of the journey (ADR-286): what it is for, in words, beside a scene that shows it. */
export function Chapter({ id, app, number, label, audience, headline, body, points, flipped, scene }: ChapterProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const active = useInView(stageRef);
  return (
    <section id={id} className={cn("chapter", flipped && "is-flipped")} data-app={app} aria-labelledby={`${id}-title`}>
      <div className="chapter-copy">
        <ChapterKicker number={number} label={label} />
        <p className="chapter-for">{audience}</p>
        <h2 id={`${id}-title`}>{headline}</h2>
        <p className="lead">{body}</p>
        <ChapterPoints points={points} />
      </div>
      <div ref={stageRef} className="chapter-stage">
        {scene(active)}
      </div>
    </section>
  );
}

export function ChapterKicker({ number, label, coming }: { number: number; label: string; coming?: boolean }) {
  return (
    <p className="kicker chapter-kicker">
      <span>{String(number).padStart(2, "0")}</span>
      {label.toUpperCase()}
      {coming && <span className="coming">{m.chapter_coming()}</span>}
    </p>
  );
}

export function ChapterPoints({ points }: { points: readonly string[] }) {
  return (
    <ul className="chapter-points">
      {points.map((point) => (
        <li key={point}>{point}</li>
      ))}
    </ul>
  );
}
